import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { adminContentAPI, imageAPI } from '../utils/api';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type TabType = 'hero' | 'schoolPassers' | 'youtube' | 'instructors' | 'instagram' | 'historyPassers';

interface HeroSection {
  imageUrls: string[];
  subtitle: string;
  title: string;
  buttonText: string;
  buttonLink: string;
}

interface SchoolPasser {
  _id?: string;
  thumbnailUrl: string;
  school: string;
  count: number;
  link: string;
  order: number;
}

interface YoutubeVideo {
  _id?: string;
  thumbnailUrl: string;
  title: string;
  description: string;
  link: string;
  order: number;
}

interface Instructor {
  _id?: string;
  imageUrl: string;
  name: string;
  description: string;
  link: string;
  order: number;
}

interface InstagramPost {
  _id?: string;
  imageUrl: string;
  title: string;
  link: string;
  order: number;
}

interface HistoryPasser {
  _id?: string;
  leftText: string;
  rightText: string;
  order: number;
}

// 드래그 가능한 강사 카드 컴포넌트
interface SortableInstructorCardProps {
  instructor: Instructor;
  index: number;
  onRemove: (index: number) => void;
  onImageUpload: (index: number, file: File) => void;
  onUpdate: (index: number, field: keyof Instructor, value: string) => void;
}

const SortableInstructorCard: React.FC<SortableInstructorCardProps> = ({
  instructor,
  index,
  onRemove,
  onImageUpload,
  onUpdate,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: instructor._id || `instructor-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <DraggableItemCard ref={setNodeRef} style={style}>
      <ItemHeader>
        <DragHandle {...attributes} {...listeners}>
          <DragIcon>&#9776;</DragIcon>
          <ItemTitle>강사 {index + 1}</ItemTitle>
        </DragHandle>
        <DeleteButton onClick={() => onRemove(index)}>삭제</DeleteButton>
      </ItemHeader>
      <FormRow>
        <FormLabel>강사 이미지</FormLabel>
        <input
          type="file"
          onChange={(e) => e.target.files?.[0] && onImageUpload(index, e.target.files[0])}
          accept="image/*"
          style={{ display: 'none' }}
          id={`instructor-img-${index}`}
        />
        <UploadButton onClick={() => document.getElementById(`instructor-img-${index}`)?.click()}>
          이미지 선택
        </UploadButton>
        {instructor.imageUrl && (
          <SmallImagePreview src={instructor.imageUrl} alt="강사 이미지" />
        )}
      </FormRow>
      <FormRow>
        <FormLabel>강사 이름</FormLabel>
        <Input
          value={instructor.name}
          onChange={(e) => onUpdate(index, 'name', e.target.value)}
          placeholder="예: 김민수 강사"
        />
      </FormRow>
      <FormRow>
        <FormLabel>소개 (학교/전공)</FormLabel>
        <Input
          value={instructor.description}
          onChange={(e) => onUpdate(index, 'description', e.target.value)}
          placeholder="예: 한국예술종합학교 출신"
        />
      </FormRow>
      <FormRow>
        <FormLabel>클릭 시 이동 링크</FormLabel>
        <Input
          value={instructor.link}
          onChange={(e) => onUpdate(index, 'link', e.target.value)}
          placeholder="예: /about/instructors"
        />
      </FormRow>
    </DraggableItemCard>
  );
};

const ContentManagePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 히어로 섹션
  const [heroSection, setHeroSection] = useState<HeroSection>({
    imageUrls: [],
    subtitle: '',
    title: '',
    buttonText: '',
    buttonLink: '',
  });

  // 학교별 합격자
  const [schoolPassers, setSchoolPassers] = useState<SchoolPasser[]>([]);

  // 유튜브 영상
  const [youtubeVideos, setYoutubeVideos] = useState<YoutubeVideo[]>([]);

  // 강사진
  const [instructors, setInstructors] = useState<Instructor[]>([]);

  // 인스타그램
  const [instagramPosts, setInstagramPosts] = useState<InstagramPost[]>([]);

  // 역대 합격자
  const [historyPassers, setHistoryPassers] = useState<HistoryPasser[]>([]);

  const heroImageRef = useRef<HTMLInputElement>(null);

  // 드래그 앤 드롭 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await adminContentAPI.getAll();
      const data = response.data.data;

      const defaultHeroSection: HeroSection = {
        imageUrls: [],
        subtitle: 'MAKE YOUR STYLE',
        title: '입시를 스타일하다, 민액터스',
        buttonText: '2024 합격자 전체보기',
        buttonLink: '/passers',
      };
      setHeroSection({
        ...defaultHeroSection,
        ...(data.heroSection || {}),
        imageUrls: data.heroSection?.imageUrls || [],
      });
      setSchoolPassers(data.schoolPassers || []);
      setYoutubeVideos(data.youtubeVideos || []);
      setInstructors(data.instructors || []);
      setInstagramPosts(data.instagramPosts || []);
      setHistoryPassers(data.historyPassers || []);
    } catch (error) {
      console.error('Error fetching content:', error);
      alert('콘텐츠를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 히어로 섹션 저장
  const handleSaveHero = async () => {
    try {
      setSaving(true);
      console.log('저장 시도:', heroSection);
      const response = await adminContentAPI.updateHero(heroSection);
      console.log('저장 성공:', response.data);
      alert('저장되었습니다.');
    } catch (error: any) {
      console.error('저장 실패:', error);
      // 인증 에러인 경우 로그인 페이지로 리다이렉트
      if (error.isAuthError) {
        alert(error.message);
        window.location.href = '/login';
        return;
      }
      alert(`저장에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setSaving(false);
    }
  };

  // 히어로 이미지 업로드 (여러 이미지 추가)
  const handleHeroImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      console.log('이미지 업로드 시도:', file.name);
      const response = await imageAPI.upload(file, 'content/hero');
      console.log('이미지 업로드 성공:', response.data);
      const newImageUrl = response.data.data.url;
      setHeroSection({
        ...heroSection,
        imageUrls: [...heroSection.imageUrls, newImageUrl],
      });
    } catch (error: any) {
      console.error('이미지 업로드 실패:', error);
      alert(`이미지 업로드에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
    }
    // 파일 입력 초기화 (같은 파일 다시 선택 가능)
    if (heroImageRef.current) {
      heroImageRef.current.value = '';
    }
  };

  // 히어로 이미지 삭제
  const handleRemoveHeroImage = (index: number) => {
    setHeroSection({
      ...heroSection,
      imageUrls: heroSection.imageUrls.filter((_, i) => i !== index),
    });
  };

  // 히어로 이미지 순서 변경
  const handleMoveHeroImage = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= heroSection.imageUrls.length) return;

    const newImageUrls = [...heroSection.imageUrls];
    [newImageUrls[index], newImageUrls[newIndex]] = [newImageUrls[newIndex], newImageUrls[index]];
    setHeroSection({ ...heroSection, imageUrls: newImageUrls });
  };

  // 학교별 합격자 저장
  const handleSaveSchoolPassers = async () => {
    try {
      setSaving(true);
      await adminContentAPI.updateSchoolPassers(schoolPassers);
      alert('저장되었습니다.');
    } catch (error: any) {
      console.error('저장 실패:', error);
      if (error.isAuthError) {
        alert(error.message);
        window.location.href = '/login';
        return;
      }
      alert(`저장에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setSaving(false);
    }
  };

  // 학교 추가
  const handleAddSchool = () => {
    setSchoolPassers([
      ...schoolPassers,
      { thumbnailUrl: '', school: '', count: 0, link: '', order: schoolPassers.length },
    ]);
  };

  // 학교 삭제
  const handleRemoveSchool = (index: number) => {
    setSchoolPassers(schoolPassers.filter((_, i) => i !== index));
  };

  // 학교 썸네일 업로드
  const handleSchoolThumbnailUpload = async (index: number, file: File) => {
    try {
      const response = await imageAPI.upload(file, 'content/schools');
      const updated = [...schoolPassers];
      updated[index].thumbnailUrl = response.data.data.url;
      setSchoolPassers(updated);
    } catch (error) {
      alert('이미지 업로드에 실패했습니다.');
    }
  };

  // 유튜브 영상 저장
  const handleSaveYoutube = async () => {
    try {
      setSaving(true);
      await adminContentAPI.updateYoutubeVideos(youtubeVideos);
      alert('저장되었습니다.');
    } catch (error: any) {
      console.error('저장 실패:', error);
      if (error.isAuthError) {
        alert(error.message);
        window.location.href = '/login';
        return;
      }
      alert(`저장에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setSaving(false);
    }
  };

  // 유튜브 추가
  const handleAddYoutube = () => {
    setYoutubeVideos([
      ...youtubeVideos,
      { thumbnailUrl: '', title: '', description: '', link: '', order: youtubeVideos.length },
    ]);
  };

  // 유튜브 삭제
  const handleRemoveYoutube = (index: number) => {
    setYoutubeVideos(youtubeVideos.filter((_, i) => i !== index));
  };

  // 유튜브 썸네일 업로드
  const handleYoutubeThumbnailUpload = async (index: number, file: File) => {
    try {
      const response = await imageAPI.upload(file, 'content/youtube');
      const updated = [...youtubeVideos];
      updated[index].thumbnailUrl = response.data.data.url;
      setYoutubeVideos(updated);
    } catch (error) {
      alert('이미지 업로드에 실패했습니다.');
    }
  };

  // 강사진 저장
  const handleSaveInstructors = async () => {
    try {
      setSaving(true);
      await adminContentAPI.updateInstructors(instructors);
      alert('저장되었습니다.');
    } catch (error: any) {
      console.error('저장 실패:', error);
      if (error.isAuthError) {
        alert(error.message);
        window.location.href = '/login';
        return;
      }
      alert(`저장에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setSaving(false);
    }
  };

  // 강사 추가
  const handleAddInstructor = () => {
    setInstructors([
      ...instructors,
      { imageUrl: '', name: '', description: '', link: '', order: instructors.length },
    ]);
  };

  // 강사 삭제
  const handleRemoveInstructor = (index: number) => {
    setInstructors(instructors.filter((_, i) => i !== index));
  };

  // 강사 순서 변경 (드래그 앤 드롭)
  const handleInstructorDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setInstructors((items) => {
        const oldIndex = items.findIndex(
          (item) => (item._id || `instructor-${items.indexOf(item)}`) === active.id
        );
        const newIndex = items.findIndex(
          (item) => (item._id || `instructor-${items.indexOf(item)}`) === over.id
        );

        const newItems = arrayMove(items, oldIndex, newIndex);
        // order 값 업데이트
        return newItems.map((item, idx) => ({ ...item, order: idx }));
      });
    }
  };

  // 강사 필드 업데이트
  const handleUpdateInstructor = (index: number, field: keyof Instructor, value: string) => {
    const updated = [...instructors];
    (updated[index] as any)[field] = value;
    setInstructors(updated);
  };

  // 강사 이미지 업로드
  const handleInstructorImageUpload = async (index: number, file: File) => {
    try {
      const response = await imageAPI.upload(file, 'content/instructors');
      const updated = [...instructors];
      updated[index].imageUrl = response.data.data.url;
      setInstructors(updated);
    } catch (error) {
      alert('이미지 업로드에 실패했습니다.');
    }
  };

  // 인스타그램 저장
  const handleSaveInstagram = async () => {
    try {
      setSaving(true);
      await adminContentAPI.updateInstagramPosts(instagramPosts);
      alert('저장되었습니다.');
    } catch (error: any) {
      console.error('저장 실패:', error);
      if (error.isAuthError) {
        alert(error.message);
        window.location.href = '/login';
        return;
      }
      alert(`저장에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setSaving(false);
    }
  };

  // 인스타 추가
  const handleAddInstagram = () => {
    setInstagramPosts([
      ...instagramPosts,
      { imageUrl: '', title: '', link: '', order: instagramPosts.length },
    ]);
  };

  // 인스타 삭제
  const handleRemoveInstagram = (index: number) => {
    setInstagramPosts(instagramPosts.filter((_, i) => i !== index));
  };

  // 인스타 이미지 업로드
  const handleInstagramImageUpload = async (index: number, file: File) => {
    try {
      const response = await imageAPI.upload(file, 'content/instagram');
      const updated = [...instagramPosts];
      updated[index].imageUrl = response.data.data.url;
      setInstagramPosts(updated);
    } catch (error) {
      alert('이미지 업로드에 실패했습니다.');
    }
  };

  // 역대 합격자 저장
  const handleSaveHistoryPassers = async () => {
    try {
      setSaving(true);
      await adminContentAPI.updateHistoryPassers(historyPassers);
      alert('저장되었습니다.');
    } catch (error: any) {
      console.error('저장 실패:', error);
      if (error.isAuthError) {
        alert(error.message);
        window.location.href = '/login';
        return;
      }
      alert(`저장에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setSaving(false);
    }
  };

  // 역대 합격자 추가 (새 항목이 맨 위에 추가)
  const handleAddHistoryPasser = () => {
    setHistoryPassers([
      { leftText: '', rightText: '', order: 0 },
      ...historyPassers,
    ]);
  };

  // 역대 합격자 삭제
  const handleRemoveHistoryPasser = (index: number) => {
    setHistoryPassers(historyPassers.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <Container>
        <PageTitle>컨텐츠 관리</PageTitle>
        <LoadingText>로딩 중...</LoadingText>
      </Container>
    );
  }

  return (
    <Container>
      <PageTitle>컨텐츠 관리</PageTitle>

      <TabContainer>
        <Tab $active={activeTab === 'hero'} onClick={() => setActiveTab('hero')}>
          메인 홈 이미지
        </Tab>
        <Tab $active={activeTab === 'schoolPassers'} onClick={() => setActiveTab('schoolPassers')}>
          학교별 합격자
        </Tab>
        <Tab $active={activeTab === 'youtube'} onClick={() => setActiveTab('youtube')}>
          유튜브 영상
        </Tab>
        <Tab $active={activeTab === 'instructors'} onClick={() => setActiveTab('instructors')}>
          강사진
        </Tab>
        <Tab $active={activeTab === 'instagram'} onClick={() => setActiveTab('instagram')}>
          학원 인스타 썸네일
        </Tab>
        <Tab $active={activeTab === 'historyPassers'} onClick={() => setActiveTab('historyPassers')}>
          역대 합격자
        </Tab>
      </TabContainer>

      <ContentBox>
        {/* 메인 홈 이미지 탭 */}
        {activeTab === 'hero' && (
          <>
            <SectionTitle>메인 홈 이미지 설정</SectionTitle>
            <FormRow>
              <FormLabel>배경 이미지 (여러 장 등록 시 캐러셀로 표시됩니다)</FormLabel>
              <input
                type="file"
                ref={heroImageRef}
                onChange={handleHeroImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <UploadButton onClick={() => heroImageRef.current?.click()}>
                + 이미지 추가
              </UploadButton>
              <ImageCountText>
                {heroSection.imageUrls.length}개의 이미지가 등록되어 있습니다.
              </ImageCountText>
              {heroSection.imageUrls.length > 0 && (
                <HeroImageGrid>
                  {heroSection.imageUrls.map((url, index) => (
                    <HeroImageItem key={index}>
                      <HeroImageNumber>{index + 1}</HeroImageNumber>
                      <img src={url} alt={`히어로 이미지 ${index + 1}`} />
                      <HeroImageActions>
                        <ImageOrderButton
                          onClick={() => handleMoveHeroImage(index, 'up')}
                          disabled={index === 0}
                          title="위로 이동"
                        >
                          &#9650;
                        </ImageOrderButton>
                        <ImageOrderButton
                          onClick={() => handleMoveHeroImage(index, 'down')}
                          disabled={index === heroSection.imageUrls.length - 1}
                          title="아래로 이동"
                        >
                          &#9660;
                        </ImageOrderButton>
                        <ImageRemoveButton onClick={() => handleRemoveHeroImage(index)}>
                          삭제
                        </ImageRemoveButton>
                      </HeroImageActions>
                    </HeroImageItem>
                  ))}
                </HeroImageGrid>
              )}
            </FormRow>
            <FormRow>
              <FormLabel>서브 타이틀</FormLabel>
              <Input
                value={heroSection.subtitle}
                onChange={(e) => setHeroSection({ ...heroSection, subtitle: e.target.value })}
                placeholder="예: MAKE YOUR STYLE"
              />
            </FormRow>
            <FormRow>
              <FormLabel>메인 타이틀</FormLabel>
              <Input
                value={heroSection.title}
                onChange={(e) => setHeroSection({ ...heroSection, title: e.target.value })}
                placeholder="예: 입시를 스타일하다, 민액터스"
              />
            </FormRow>
            <FormRow>
              <FormLabel>버튼 텍스트</FormLabel>
              <Input
                value={heroSection.buttonText}
                onChange={(e) => setHeroSection({ ...heroSection, buttonText: e.target.value })}
                placeholder="예: 2024 합격자 전체보기"
              />
            </FormRow>
            <FormRow>
              <FormLabel>버튼 링크</FormLabel>
              <Input
                value={heroSection.buttonLink}
                onChange={(e) => setHeroSection({ ...heroSection, buttonLink: e.target.value })}
                placeholder="예: /passers"
              />
            </FormRow>
            <SaveButtonContainer>
              <SaveButton onClick={handleSaveHero} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </SaveButton>
            </SaveButtonContainer>
          </>
        )}

        {/* 학교별 합격자 탭 */}
        {activeTab === 'schoolPassers' && (
          <>
            <SectionHeader>
              <SectionTitle>학교별 합격자 설정</SectionTitle>
              <AddButton onClick={handleAddSchool}>+ 학교 추가</AddButton>
            </SectionHeader>
            {schoolPassers.map((school, index) => (
              <ItemCard key={index}>
                <ItemHeader>
                  <ItemTitle>학교 {index + 1}</ItemTitle>
                  <DeleteButton onClick={() => handleRemoveSchool(index)}>삭제</DeleteButton>
                </ItemHeader>
                <FormRow>
                  <FormLabel>썸네일 이미지</FormLabel>
                  <input
                    type="file"
                    onChange={(e) => e.target.files?.[0] && handleSchoolThumbnailUpload(index, e.target.files[0])}
                    accept="image/*"
                    style={{ display: 'none' }}
                    id={`school-thumb-${index}`}
                  />
                  <UploadButton onClick={() => document.getElementById(`school-thumb-${index}`)?.click()}>
                    이미지 선택
                  </UploadButton>
                  {school.thumbnailUrl && (
                    <SmallImagePreview src={school.thumbnailUrl} alt="썸네일" />
                  )}
                </FormRow>
                <FormRow>
                  <FormLabel>학교명</FormLabel>
                  <Input
                    value={school.school}
                    onChange={(e) => {
                      const updated = [...schoolPassers];
                      updated[index].school = e.target.value;
                      setSchoolPassers(updated);
                    }}
                    placeholder="예: 한국예술종합학교"
                  />
                </FormRow>
                <FormRow>
                  <FormLabel>합격자 수</FormLabel>
                  <Input
                    type="number"
                    min="0"
                    value={school.count === 0 ? '' : school.count}
                    onChange={(e) => {
                      const updated = [...schoolPassers];
                      const value = e.target.value;
                      updated[index].count = value === '' ? 0 : Number(value);
                      setSchoolPassers(updated);
                    }}
                    placeholder="0"
                  />
                </FormRow>
                <FormRow>
                  <FormLabel>클릭 시 이동 링크</FormLabel>
                  <Input
                    value={school.link}
                    onChange={(e) => {
                      const updated = [...schoolPassers];
                      updated[index].link = e.target.value;
                      setSchoolPassers(updated);
                    }}
                    placeholder="예: /passers/karts"
                  />
                </FormRow>
              </ItemCard>
            ))}
            <SaveButtonContainer>
              <SaveButton onClick={handleSaveSchoolPassers} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </SaveButton>
            </SaveButtonContainer>
          </>
        )}

        {/* 유튜브 영상 탭 */}
        {activeTab === 'youtube' && (
          <>
            <SectionHeader>
              <SectionTitle>유튜브 영상 설정</SectionTitle>
              <AddButton onClick={handleAddYoutube}>+ 영상 추가</AddButton>
            </SectionHeader>
            {youtubeVideos.map((video, index) => (
              <ItemCard key={index}>
                <ItemHeader>
                  <ItemTitle>영상 {index + 1}</ItemTitle>
                  <DeleteButton onClick={() => handleRemoveYoutube(index)}>삭제</DeleteButton>
                </ItemHeader>
                <FormRow>
                  <FormLabel>썸네일 이미지</FormLabel>
                  <input
                    type="file"
                    onChange={(e) => e.target.files?.[0] && handleYoutubeThumbnailUpload(index, e.target.files[0])}
                    accept="image/*"
                    style={{ display: 'none' }}
                    id={`youtube-thumb-${index}`}
                  />
                  <UploadButton onClick={() => document.getElementById(`youtube-thumb-${index}`)?.click()}>
                    이미지 선택
                  </UploadButton>
                  {video.thumbnailUrl && (
                    <SmallImagePreview src={video.thumbnailUrl} alt="썸네일" />
                  )}
                </FormRow>
                <FormRow>
                  <FormLabel>합격생 이름</FormLabel>
                  <Input
                    value={video.title}
                    onChange={(e) => {
                      const updated = [...youtubeVideos];
                      updated[index].title = e.target.value;
                      setYoutubeVideos(updated);
                    }}
                    placeholder="예: 김영희"
                  />
                </FormRow>
                <FormRow>
                  <FormLabel>서브 타이틀</FormLabel>
                  <Input
                    value={video.description}
                    onChange={(e) => {
                      const updated = [...youtubeVideos];
                      updated[index].description = e.target.value;
                      setYoutubeVideos(updated);
                    }}
                    placeholder="예: 2024 합격생 인터뷰"
                  />
                </FormRow>
                <FormRow>
                  <FormLabel>영상 링크</FormLabel>
                  <Input
                    value={video.link}
                    onChange={(e) => {
                      const updated = [...youtubeVideos];
                      updated[index].link = e.target.value;
                      setYoutubeVideos(updated);
                    }}
                    placeholder="예: https://youtube.com/watch?v=..."
                  />
                </FormRow>
              </ItemCard>
            ))}
            <SaveButtonContainer>
              <SaveButton onClick={handleSaveYoutube} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </SaveButton>
            </SaveButtonContainer>
          </>
        )}

        {/* 강사진 탭 */}
        {activeTab === 'instructors' && (
          <>
            <SectionHeader>
              <SectionTitle>강사진 설정</SectionTitle>
              <HeaderActions>
                <AddButton onClick={handleAddInstructor}>+ 강사 추가</AddButton>
                <SaveButton onClick={handleSaveInstructors} disabled={saving}>
                  {saving ? '저장 중...' : '저장'}
                </SaveButton>
              </HeaderActions>
            </SectionHeader>
            <DragHint>카드 왼쪽의 &#9776; 아이콘을 드래그하여 순서를 변경할 수 있습니다.</DragHint>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleInstructorDragEnd}
            >
              <SortableContext
                items={instructors.map((inst, idx) => inst._id || `instructor-${idx}`)}
                strategy={verticalListSortingStrategy}
              >
                {instructors.map((instructor, index) => (
                  <SortableInstructorCard
                    key={instructor._id || `instructor-${index}`}
                    instructor={instructor}
                    index={index}
                    onRemove={handleRemoveInstructor}
                    onImageUpload={handleInstructorImageUpload}
                    onUpdate={handleUpdateInstructor}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </>
        )}

        {/* 인스타그램 탭 */}
        {activeTab === 'instagram' && (
          <>
            <SectionHeader>
              <SectionTitle>학원 인스타 썸네일 설정</SectionTitle>
              <AddButton onClick={handleAddInstagram}>+ 포스트 추가</AddButton>
            </SectionHeader>
            {instagramPosts.map((post, index) => (
              <ItemCard key={index}>
                <ItemHeader>
                  <ItemTitle>포스트 {index + 1}</ItemTitle>
                  <DeleteButton onClick={() => handleRemoveInstagram(index)}>삭제</DeleteButton>
                </ItemHeader>
                <FormRow>
                  <FormLabel>썸네일 이미지</FormLabel>
                  <input
                    type="file"
                    onChange={(e) => e.target.files?.[0] && handleInstagramImageUpload(index, e.target.files[0])}
                    accept="image/*"
                    style={{ display: 'none' }}
                    id={`instagram-img-${index}`}
                  />
                  <UploadButton onClick={() => document.getElementById(`instagram-img-${index}`)?.click()}>
                    이미지 선택
                  </UploadButton>
                  {post.imageUrl && (
                    <SmallImagePreview src={post.imageUrl} alt="인스타 이미지" />
                  )}
                </FormRow>
                <FormRow>
                  <FormLabel>제목 (선택)</FormLabel>
                  <Input
                    value={post.title}
                    onChange={(e) => {
                      const updated = [...instagramPosts];
                      updated[index].title = e.target.value;
                      setInstagramPosts(updated);
                    }}
                    placeholder="예: 합격 후기"
                  />
                </FormRow>
                <FormRow>
                  <FormLabel>인스타그램 링크</FormLabel>
                  <Input
                    value={post.link}
                    onChange={(e) => {
                      const updated = [...instagramPosts];
                      updated[index].link = e.target.value;
                      setInstagramPosts(updated);
                    }}
                    placeholder="예: https://instagram.com/p/..."
                  />
                </FormRow>
              </ItemCard>
            ))}
            <SaveButtonContainer>
              <SaveButton onClick={handleSaveInstagram} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </SaveButton>
            </SaveButtonContainer>
          </>
        )}

        {/* 역대 합격자 탭 */}
        {activeTab === 'historyPassers' && (
          <>
            <SectionHeader>
              <SectionTitle>역대 합격자 설정</SectionTitle>
              <AddButton onClick={handleAddHistoryPasser}>+ 합격자 추가</AddButton>
            </SectionHeader>
            {historyPassers.map((passer, index) => (
              <ItemCard key={index}>
                <ItemHeader>
                  <ItemTitle>합격자 {index + 1}</ItemTitle>
                  <DeleteButton onClick={() => handleRemoveHistoryPasser(index)}>삭제</DeleteButton>
                </ItemHeader>
                <FormRow>
                  <FormLabel>좌측 텍스트 (학교 연도)</FormLabel>
                  <Input
                    value={passer.leftText}
                    onChange={(e) => {
                      const updated = [...historyPassers];
                      updated[index].leftText = e.target.value;
                      setHistoryPassers(updated);
                    }}
                    placeholder="예: 한국예술종합학교 25학년도"
                  />
                </FormRow>
                <FormRow>
                  <FormLabel>우측 텍스트 (이름)</FormLabel>
                  <Input
                    value={passer.rightText}
                    onChange={(e) => {
                      const updated = [...historyPassers];
                      updated[index].rightText = e.target.value;
                      setHistoryPassers(updated);
                    }}
                    placeholder="예: 이찬민"
                  />
                </FormRow>
              </ItemCard>
            ))}
            <SaveButtonContainer>
              <SaveButton onClick={handleSaveHistoryPassers} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </SaveButton>
            </SaveButtonContainer>
          </>
        )}
      </ContentBox>
    </Container>
  );
};

// Styled Components
const Container = styled.div``;

const PageTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #333;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 20px;
  background: ${(props) => (props.$active ? '#4dabf7' : 'transparent')};
  color: ${(props) => (props.$active ? 'white' : '#666')};
  border: none;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.$active ? '#4dabf7' : '#f0f0f0')};
  }
`;

const ContentBox = styled.div`
  background: white;
  border-radius: 8px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
`;

const FormRow = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #4dabf7;
  }
`;

const UploadButton = styled.button`
  padding: 10px 20px;
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  margin-bottom: 12px;

  &:hover {
    background: #e9ecef;
  }
`;

const ImagePreview = styled.div`
  margin-top: 12px;
  position: relative;
  display: inline-block;

  img {
    max-width: 300px;
    max-height: 200px;
    border-radius: 6px;
    border: 1px solid #ddd;
  }
`;

const SmallImagePreview = styled.img`
  max-width: 150px;
  max-height: 100px;
  border-radius: 4px;
  border: 1px solid #ddd;
  margin-top: 8px;
  display: block;
`;

const ImageCountText = styled.p`
  font-size: 13px;
  color: #666;
  margin: 8px 0 16px 0;
`;

const HeroImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 12px;
`;

const HeroImageItem = styled.div`
  position: relative;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  background: #f9f9f9;

  img {
    width: 100%;
    height: 150px;
    object-fit: cover;
  }
`;

const HeroImageNumber = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const HeroImageActions = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px;
  background: #f9f9f9;
  justify-content: center;
`;

const ImageOrderButton = styled.button`
  padding: 6px 10px;
  background: #e9ecef;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
  color: #333;

  &:hover:not(:disabled) {
    background: #dee2e6;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ImageRemoveButton = styled.button`
  padding: 6px 12px;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;

  &:hover {
    background: #fa5252;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px 12px;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #fa5252;
  }
`;

const AddButton = styled.button`
  padding: 10px 20px;
  background: #4dabf7;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: #339af0;
  }
`;

const ItemCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
`;

const DraggableItemCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: #4dabf7;
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: grab;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }

  &:active {
    cursor: grabbing;
  }
`;

const DragIcon = styled.span`
  font-size: 16px;
  color: #999;
  user-select: none;
`;

const DragHint = styled.p`
  font-size: 13px;
  color: #888;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #4dabf7;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
`;

const ItemTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const DeleteButton = styled.button`
  padding: 6px 12px;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #fa5252;
  }
`;

const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const SaveButton = styled.button`
  padding: 12px 32px;
  background: #4dabf7;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #339af0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default ContentManagePage;
